import styled from '@emotion/styled';
import { useEffect, useRef } from 'react';

import { useDynaTracking } from '~/common/hooks/useDynaTracking';
import { useNavSearchStore } from '~/stores/useNavSearchStore';

import { SearchInput } from './SearchInput';

type NavAlertItemsContainerProps = {
  ariaLabel: string;
  children: React.ReactNode;
  enableSearch?: boolean;
  noResults?: boolean;
};

const NoResultsMessage = styled('p')`
  padding: 1rem 0.75rem;
  color: ${({ theme }) => theme.colors.gray[500]};
  font-size: 0.875rem;
  text-align: center;
  background-color: white;
`;

function handleArrowNavigation(e: KeyboardEvent) {
  if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
  e.preventDefault();

  const nav = e.currentTarget as HTMLElement;
  const focusable = Array.from(
    nav.querySelectorAll<HTMLElement>('input, button'),
  );
  const currentIndex = focusable.indexOf(document.activeElement as HTMLElement);

  if (e.key === 'ArrowDown') {
    focusable[currentIndex + 1]?.focus();
  } else {
    focusable[currentIndex - 1]?.focus();
  }
}

export default function NavAlertItemsContainer({
  ariaLabel,
  children,
  enableSearch = false,
  noResults = false,
}: Readonly<NavAlertItemsContainerProps>) {
  const setSearchQuery = useNavSearchStore((s) => s.setSearchQuery);
  const { sendNavSearchClickEvent } = useDynaTracking();
  const navRef = useRef<HTMLElement>(null);

  useEffect(() => () => setSearchQuery(''), [setSearchQuery]);

  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return;
    nav.addEventListener('keydown', handleArrowNavigation);
    return () => nav.removeEventListener('keydown', handleArrowNavigation);
  }, []);

  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return;

    function handleClick(e: MouseEvent) {
      const searchQuery = useNavSearchStore.getState().searchQuery;

      if (!searchQuery) return;
      const button = (e.target as HTMLElement).closest('button');
      if (!button) return;
      const buttonName =
        button.getAttribute('aria-label') ?? button.textContent ?? '';
      sendNavSearchClickEvent({
        buttonName,
        searchQuery,
      });
    }

    nav.addEventListener('click', handleClick);
    return () => nav.removeEventListener('click', handleClick);
  }, [sendNavSearchClickEvent]);

  return (
    <nav
      ref={navRef}
      aria-label={ariaLabel}
      style={{
        height: '100%',
        minHeight: 0,
        overflow: 'hidden',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'auto',
        scrollbarWidth: 'thin',
      }}
    >
      {enableSearch && <SearchInput />}
      {noResults ? (
        <NoResultsMessage>No matching pages found</NoResultsMessage>
      ) : (
        children
      )}
    </nav>
  );
}
